import React, {useEffect, useMemo, useState} from 'react';
import styles from "./App.module.scss";
import {CustomLink} from "./components";
import {RouteDictionary} from "./utils";
import mintsifry from "assets/images/mintsifry.png";
import logoSkolkovo from "assets/images/logo-skolkovo.png";
import svetlanaVasina from "assets/images/team/svetlana-vasina.jpg";
import kirillPanov from "assets/images/team/kirill-panov.jpg";
import irinaYumanova from "assets/images/team/irina-yumanova.jpg";
import mihailKirsanov from "assets/images/team/mihail-kirsanov.jpg";

const ANNUAL_RATE = 0.3;
const MONTHS_IN_YEAR = 12;

const formatMoney = (value: number) => `${Math.round(value).toLocaleString('ru-RU')} ₽`;

const pluralizeMonths = (months: number) => {
  const forms = ['месяц', 'месяца', 'месяцев'];
  const mod100 = months % 100;
  const mod10 = months % 10;
  if (mod100 > 4 && mod100 < 20) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 > 1 && mod10 < 5) return forms[1];
  return forms[2];
};

const Icon = {
  person: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.2"/>
      <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0"/>
    </svg>
  ),
  document: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3.5h8l4 4V20.5H6z"/>
      <path d="M14 3.5V8h4"/>
      <path d="M9 12.5h6M9 15.5h4"/>
    </svg>
  ),
  people: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="2.6"/>
      <path d="M3.5 18.5a5.5 5.5 0 0 1 11 0"/>
      <path d="M16.2 6.6a2.5 2.5 0 0 1 0 4.8"/>
      <path d="M17 13.3a5.5 5.5 0 0 1 3.5 5.2"/>
    </svg>
  ),
  card: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="6" width="17" height="12" rx="2"/>
      <path d="M3.5 10h17"/>
      <circle cx="16.5" cy="14" r="1.1"/>
    </svg>
  ),
  checkCircle: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.5"/>
      <path d="M8.5 12.2 11 14.7 15.7 9.4"/>
    </svg>
  ),
  arrow: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h15M13 6l6 6-6 6"/>
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3 5 5.8v5.2c0 4.2 3 6.8 7 8.2 4-1.4 7-4 7-8.2V5.8L12 3Z"/>
      <path d="M9 11.5 11.2 13.7 15.2 9.3"/>
    </svg>
  ),
  bars: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17V9M12 17V5M19 17v-5"/>
      <path d="M3.5 20.5h17"/>
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 8v4.5"/>
      <path d="M12 16h.01"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5"/>
    </svg>
  ),
  bank: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9.5 12 4l9 5.5"/>
      <path d="M4 9.8h16"/>
      <path d="M5.5 9.8V18M9.8 9.8V18M14.2 9.8V18M18.5 9.8V18"/>
      <path d="M3.5 18h17"/>
    </svg>
  ),
  scales: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4.5V20"/>
      <path d="M8 20h8"/>
      <path d="M4.5 7h15"/>
      <circle cx="12" cy="4" r="1.3"/>
      <path d="M4.5 7 2 12.4a2.9 2.9 0 0 0 5 0L4.5 7Z"/>
      <path d="M19.5 7 17 12.4a2.9 2.9 0 0 0 5 0L19.5 7Z"/>
    </svg>
  ),
};

const dealFlow = [
  {
    icon: Icon.person,
    title: <>Проверка заёмщика и&nbsp;контрагента</>,
    text: <>Проверяем заёмщика и&nbsp;контрагента, который будет оплачивать поставку.</>,
  },
  {
    icon: Icon.document,
    title: <>Обеспечительная уступка требования</>,
    text: <>Право требования на&nbsp;будущую оплату контрагента становится обеспечением по&nbsp;сделке займа.</>,
  },
  {
    icon: Icon.people,
    title: <>Инвесторы финансируют сделку</>,
    text: <>Поставщик получает оплату по&nbsp;сделке сразу по&nbsp;договору между ним и&nbsp;инвесторами.</>,
  },
  {
    icon: Icon.card,
    title: <>Контрагент исполняет обязательства</>,
    text: <>Контрагент оплачивает задолженность перед поставщиком напрямую инвесторам
      на&nbsp;номинальный счёт платформы.</>,
  },
  {
    icon: Icon.checkCircle,
    title: <>Полное сопровождение цикла сделки</>,
    text: <>В&nbsp;случае задержки оплат Кономика берёт на&nbsp;себя полное взаимодействие по&nbsp;взысканию.</>,
  },
];

const riskLevels = [
  {
    title: <>Проверка на&nbsp;стоп-факторы</>,
    text: <>Отсекаем сделки, которые не&nbsp;проходят по&nbsp;базовым требованиям к&nbsp;заёмщику
      и&nbsp;контрагенту: стоп-факторы, стандартные для&nbsp;кредитных учреждений, и&nbsp;требования 115-ФЗ.</>,
  },
  {
    title: <>Проверка платёжеспособности заёмщика и&nbsp;контрагента</>,
    text: <>Автоматический скоринг по&nbsp;данным государственных информационных систем и&nbsp;баз данных.
      Используется система Rescore&nbsp;— входит в&nbsp;Группу Conomica.</>,
  },
  {
    title: <>Проверка договорной обвязки</>,
    text: <>Юристы проверяют контракт между заёмщиком и&nbsp;его контрагентом и&nbsp;достоверность
      обязательства, из&nbsp;которого возникает оплата.</>,
  },
  {
    title: <>Подтверждение от&nbsp;контрагента</>,
    text: <>Контрагент заёмщика&nbsp;(дебитор) подтверждает отсутствие возражений по&nbsp;оплате поставки.</>,
  },
  {
    title: <>Обеспечительная уступка права требования</>,
    text: <>Заёмщик подписывает обеспечительную уступку права требования к&nbsp;своему контрагенту
      в&nbsp;пользу платформы&nbsp;— это обеспечение по&nbsp;сделке.</>,
  },
];

const collectionLevels = [
  {
    title: <>Досудебная работа</>,
    text: <>Претензии должнику, поручителям и&nbsp;залогодателям, переговоры и&nbsp;реструктуризация&nbsp;—
      контроль рисков взыскания. На&nbsp;этом этапе взыскание для&nbsp;инвестора бесплатно.</>,
  },
  {
    title: <>Суд и&nbsp;исполнительное производство</>,
    text: <>Иск, судебное решение, работа с&nbsp;приставами, обращение взыскания на&nbsp;обеспечение сделки.</>,
  },
  {
    title: <>Банкротство и&nbsp;субсидиарная ответственность</>,
    text: <>Заявление о&nbsp;банкротстве должника, включение в&nbsp;реестр кредиторов, привлечение
      контролирующих лиц к&nbsp;ответственности.</>,
  },
  {
    title: <>Расчёт с&nbsp;инвестором</>,
    text: <>Взысканные средства поступают на&nbsp;номинальный счёт и&nbsp;распределяются между инвесторами
      пропорционально их&nbsp;требованиям.</>,
  },
];

const startSteps = [
  {
    title: 'Зарегистрируйтесь',
    text: <>Регистрация онлайн, без визита в&nbsp;офис.</>,
  },
  {
    title: 'Пополните счёт',
    text: <>От&nbsp;10&nbsp;000&nbsp;₽ на&nbsp;номинальный счёт в&nbsp;банке.
      У&nbsp;платформы нет доступа к&nbsp;вашим деньгам.</>,
  },
  {
    title: 'Выберите проект',
    text: <>Каждая сделка с&nbsp;обеспечением и&nbsp;проверкой по&nbsp;5&nbsp;уровням.
      Видны срок, доходность и&nbsp;обеспечение.</>,
  },
  {
    title: 'Получайте выплаты',
    text: <>Деньги возвращаются из&nbsp;оплаты контрагента, доход поступает на&nbsp;ваш счёт.</>,
  },
];

const projectRows = [
  {label: <>Контрагент (плательщик)</>, value: <span className={styles.app__blurNda}>«Белоруснефть-Сибирь»</span>},
  {label: <>Сумма финансирования</>, value: <>16&nbsp;083&nbsp;396&nbsp;₽</>},
  {label: <>Сумма обеспечения</>, value: <>22&nbsp;200&nbsp;773&nbsp;₽</>},
  {label: <>Доходность инвестора</>, value: <>26% годовых</>, green: true},
  {label: <>Срок сделки</>, value: <>3 месяца</>},
  {label: <>Покрытие обеспечением (LTV)</>, value: <>72%</>},
  {label: <>Оплата контрагента</>, value: <>на&nbsp;номинальный счёт платформы</>},
];

const metrics = [
  {value: <>9 лет</>, label: <>опыт взыскания, Группа Conomica</>},
  {value: <>450+</>, label: <>сделок с&nbsp;долговыми требованиями</>},
  {value: <>8 млрд&nbsp;₽</>, label: <>взыскано, «Про&nbsp;Фактор»</>},
  {value: <>700+</>, label: <>арбитражных дел</>},
];

const epochs = [
  {
    range: '2017 — 2020',
    title: 'Экспертиза',
    text: <>Собрали ядро: факторинг, скоринг и&nbsp;судебное взыскание долгов.</>,
    chips: ['ФК «Содружество»', 'Технологии скоринга', 'Про Фактор'],
  },
  {
    range: '2021 — 2022',
    title: 'Технология',
    text: <>Построили IT-платформу и&nbsp;CRM для&nbsp;полного цикла сделок с&nbsp;дебиторкой.</>,
    chips: ['Платформа «Кономика»', 'Резидент Сколково', 'Роспатент'],
  },
  {
    range: '2024',
    title: 'Инвестиционная платформа',
    text: <>Вышли в&nbsp;займы под&nbsp;залог дебиторки и&nbsp;объединили компании в&nbsp;группу.</>,
    chips: ['Лицензия ЦБ РФ', 'УК «Кономика»'],
    hero: true,
  },
];

const protections = [
  {
    icon: Icon.bank,
    title: 'Номинальный счёт',
    text: <>Средства инвесторов размещаются на&nbsp;номинальном счёте (ст.&nbsp;860.1 ГК). Они обособлены
      от&nbsp;имущества оператора: взыскание по&nbsp;его обязательствам на&nbsp;них не&nbsp;обращается.</>,
  },
  {
    icon: Icon.scales,
    title: <>В&nbsp;реестре Банка России</>,
    text: <>Оператор «Кономика-займы для&nbsp;бизнеса» в&nbsp;составе Группы Conomica включён в&nbsp;реестр
      операторов инвестиционных платформ Банка России. Деятельность ведётся по&nbsp;259-ФЗ.</>,
  },
  {
    icon: Icon.shield,
    title: <>Обеспечение по&nbsp;каждой сделке</>,
    text: <>По&nbsp;каждой сделке оформляется обеспечение&nbsp;— залог прав требования или обеспечительная
      уступка дебиторской задолженности&nbsp;— до&nbsp;выдачи финансирования.</>,
  },
];

const disclosureFacts = [
  <>Бухгалтерская отчётность заёмщика.</>,
  <>Бенефициары и&nbsp;органы управления заёмщика.</>,
  <>Предмет и&nbsp;условия обеспечения по&nbsp;сделке.</>,
  <>Ставка, срок и&nbsp;комиссии&nbsp;— до&nbsp;вложения.</>,
  <>Суммы уже привлечённых инвестиций и&nbsp;статус их&nbsp;исполнения.</>,
  <>Информация о&nbsp;контрагенте и&nbsp;обеспечении по&nbsp;сделке.</>,
];

const team = [
  {photo: svetlanaVasina, name: 'Светлана Васина', role: <>Генеральный директор</>},
  {photo: kirillPanov, name: 'Кирилл Панов', role: <>Управляющий партнёр</>},
  {
    photo: irinaYumanova,
    name: 'Ирина Юманова',
    role: <>Директор по&nbsp;финансам и&nbsp;инвестициям</>,
    photoPosition: 'center 5%',
  },
  {photo: mihailKirsanov, name: 'Михаил Кирсанов', role: <>Бизнес-лидер Rescore.online</>},
];

const Calculator = () => {
  const [amount, setAmount] = useState(2740000);
  const [term, setTerm] = useState(4);

  const income = useMemo(() => amount * ANNUAL_RATE * (term / MONTHS_IN_YEAR), [amount, term]);

  return (
    <>
      <div className={styles.app__calc}>
        <div className={styles.app__calcControls}>
          <div className={styles.app__calcField}>
            <div className={styles.app__calcFieldTop}>
              <span className={styles.app__calcLabel}>Сумма инвестиций</span>
              <span className={styles.app__calcValue}>{formatMoney(amount)}</span>
            </div>
            <input
              type="range"
              className={styles.app__calcRange}
              min={10000}
              max={3000000}
              step={10000}
              value={amount}
              aria-label="Сумма инвестиций"
              onChange={(e) => setAmount(Number(e.target.value))}
            />
            <div className={styles.app__calcRangeMeta}>
              <span>10 000 ₽</span>
              <span>3 000 000 ₽</span>
            </div>
          </div>

          <div className={styles.app__calcField}>
            <div className={styles.app__calcFieldTop}>
              <span className={styles.app__calcLabel}>Срок инвестиций</span>
              <span className={styles.app__calcValue}>{term} {pluralizeMonths(term)}</span>
            </div>
            <input
              type="range"
              className={styles.app__calcRange}
              min={2}
              max={5}
              step={1}
              value={term}
              aria-label="Срок инвестиций"
              onChange={(e) => setTerm(Number(e.target.value))}
            />
            <div className={styles.app__calcRangeMeta}>
              <span>2 месяца</span>
              <span>5 месяцев</span>
            </div>
          </div>
        </div>

        <div className={styles.app__calcResult}>
          <div className={styles.app__calcResultLabel}>Прогноз дохода за период</div>
          <div className={styles.app__calcIncome}>{formatMoney(income)}</div>
          <div className={styles.app__calcTotalLabel}>Итого на счёте</div>
          <div className={styles.app__calcTotal}>{formatMoney(amount + income)}</div>
          <CustomLink
            to={RouteDictionary.ENTER}
            className={`${styles.app__btn} ${styles['app__btn--white']}`}
          >
            Начать инвестировать
          </CustomLink>
        </div>
      </div>
      <p className={styles.app__calcDisclaimer}>
        Расчёт по&nbsp;верхней ставке до&nbsp;30%&nbsp;годовых. Доходность не&nbsp;гарантирована, носит
        прогнозный характер и&nbsp;может отличаться от&nbsp;фактической.
      </p>
    </>
  );
};

const App = () => {
  useEffect(() => {
    if (window.location.hash === '#how') {
      document.getElementById('how')?.scrollIntoView({behavior: 'smooth'});
    }
  }, []);

  return (
    <div className={styles.app}>
      <section className={styles.app__hero} id="hero">
        <div className={styles.app__heroBg} aria-hidden="true">
          <span className={`${styles.app__glow} ${styles['app__glow--one']}`}/>
          <span className={`${styles.app__glow} ${styles['app__glow--two']}`}/>
        </div>
        <div className={`${styles.app__container} ${styles.app__heroGrid}`}>
          <div>
            <div className={styles.app__heroBadges}>
              <span className={styles.app__badge}>
                Оператор Платформы состоит в&nbsp;реестре Банка России
              </span>
            </div>
            <h1 className={styles.app__heroTitle}>Финансирование под обеспечение дебиторской задолженности</h1>
            <p className={styles.app__heroLead}>Продукт от группы компании Кономика.</p>
            <div className={styles.app__ctaRow}>
              <CustomLink
                to={RouteDictionary.ENTER}
                className={`${styles.app__btn} ${styles['app__btn--primary']}`}
              >
                Начать инвестировать
              </CustomLink>
              <CustomLink
                to={RouteDictionary.ENTER_BORROWER}
                className={`${styles.app__btn} ${styles['app__btn--outline']}`}
              >
                Привлечь финансирование
              </CustomLink>
            </div>
          </div>

          <div className={styles.app__heroCard}>
            <div className={styles.app__heroCardLabel}>Прогнозная доходность в сделках</div>
            <div className={styles.app__heroCardFig}>
              <div className={styles.app__heroCardBig}>до&nbsp;30%</div>
              <div className={styles.app__heroCardSub}>годовых</div>
            </div>
            <div className={styles.app__heroCardMini}>
              <div className={styles.app__heroMiniItem}>
                <div className={styles.app__heroMiniValue}>от&nbsp;10&nbsp;000&nbsp;₽</div>
                <div className={styles.app__heroMiniLabel}>порог входа</div>
              </div>
              <div className={styles.app__heroMiniItem}>
                <div className={styles.app__heroMiniValue}>9 лет</div>
                <div className={styles.app__heroMiniLabel}>компетенций группы Кономика</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`${styles.app__section} ${styles['app__section--soft']}`} id="dealcycle">
        <div className={styles.app__container}>
          <div className={styles.app__secHead}>
            <span className={styles.app__badge}>Механика сделок</span>
            <h2 className={styles.app__secTitle}>
              Один цикл сделки&nbsp;— <span className={styles.app__accent}>от поставки до выплаты</span>
            </h2>
            <p className={styles.app__secText}>
              Поставщик отгрузил товар контрагенту с&nbsp;отсрочкой оплаты 30–90&nbsp;дней. Инвесторы через
              Платформу финансируют Поставщика под&nbsp;обеспечение оплатой контрагента. Поставщик уступает
              Платформе право требования и&nbsp;Контрагент платит напрямую Платформе.
            </p>
          </div>

          <div className={styles.app__mcCard}>
            <div className={styles.app__mcKicker}>Модель Conomica</div>
            <h3 className={styles.app__mcTitle}>Как работает факторинговая сделка</h3>
            <div className={styles.app__dealflow}>
              {dealFlow.map((step, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <div className={styles.app__dfArrow}>{Icon.arrow}</div>}
                  <div className={styles.app__dfStep}>
                    <div className={styles.app__dfIco}>{step.icon}</div>
                    <div className={styles.app__dfNum}>{index + 1}</div>
                    <div className={styles.app__dfT}>{step.title}</div>
                    <div className={styles.app__dfD}>{step.text}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className={styles.app__mcReturn}>
              <span className={styles.app__ruble}>₽</span>
              <span>
                Право требования к&nbsp;контрагенту как&nbsp;обеспечение сделки и&nbsp;защита интересов
                инвесторов на&nbsp;всех этапах.
              </span>
            </div>
          </div>

          <div className={styles.app__mcPriority}>
            <span className={styles.app__mpIc}>{Icon.shield}</span>
            <div>
              <h3>Операционную работу берёт на&nbsp;себя платформа</h3>
              <p>
                Мы&nbsp;берём на&nbsp;себя анализ рисков, юридическое оформление и&nbsp;контроль
                выплат&nbsp;— инвестору остаётся выбрать сделку и&nbsp;получать доход по&nbsp;графику.
              </p>
            </div>
          </div>

          <div className={styles.app__mcDelay}>
            <div className={styles.app__mdHead}>
              <h3>Где обычный цикл останавливается, наш&nbsp;— продолжается</h3>
              <p>
                Высокого дохода без&nbsp;риска не&nbsp;бывает. Поэтому важно, что происходит
                при&nbsp;задержке оплаты.
              </p>
            </div>
            <div className={styles.app__mcDelayList}>
              <div className={styles.app__mdItem}>
                <span className={styles.app__miIc}>{Icon.bars}</span>
                <p>При&nbsp;задержке оплаты на&nbsp;сумму начисляются повышенные проценты.</p>
              </div>
              <div className={styles.app__mdItem}>
                <span className={styles.app__miIc}>{Icon.alert}</span>
                <p>
                  Сведены к&nbsp;минимуму риски этапа взыскания. Инвестор не&nbsp;остаётся один на&nbsp;один
                  с&nbsp;просроченными платежами, а&nbsp;этап взыскания прозрачен.
                </p>
              </div>
              <div className={styles.app__mdItem}>
                <span className={`${styles.app__miIc} ${styles['app__miIc--strong']}`}>{Icon.check}</span>
                <p>
                  В&nbsp;Conomica-займы работу с&nbsp;долгом продолжает профессиональный
                  взыскатель <b>«Про&nbsp;Фактор»</b> (входит в&nbsp;группу Кономика).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.app__section} id="risk">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--center']} ${styles['app__secHead--wide']}`}>
            <span className={styles.app__badge}>Оценка рисков</span>
            <h2 className={styles.app__secTitle}>
              Ответственный подход к&nbsp;
              <span className={styles.app__accent}>оценке кредитных рисков</span>
            </h2>
            <p className={styles.app__secText}>
              Прежде чем попасть на&nbsp;платформу, каждая сделка проходит полноценный андеррайтинг,
              включающий в&nbsp;себя как автоматический скоринг, так и&nbsp;проверку в&nbsp;ручном режиме.
            </p>
          </div>

          <div className={styles.app__riskGrid}>
            <div className={styles.app__riskBig}>
              <div className={styles.app__rbValue}>5</div>
              <div className={styles.app__rbLabel}>шагов, которые проходит сделка перед размещением</div>
              <div className={styles.app__rbCut}>
                <b>Проводится проверка</b>
                заёмщика, контрагента заёмщика&nbsp;(дебитора) и&nbsp;связующего их&nbsp;контракта
              </div>
            </div>
            <div className={styles.app__levels}>
              {riskLevels.map((level, index) => (
                <div className={styles.app__level} key={index}>
                  <div className={styles.app__lvNum}>{index + 1}</div>
                  <div>
                    <h3>{level.title}</h3>
                    <p>{level.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`${styles.app__ltvNote} ${styles['app__ltvNote--spaced']}`}>
            <span className={styles.app__ltvIco}>{Icon.shield}</span>
            <p>
              <b>Что раскрывается по&nbsp;заёмщику.</b> До&nbsp;вложения по&nbsp;каждой сделке доступны
              бухгалтерская отчётность, сведения о&nbsp;бенефициарах и&nbsp;суммы уже привлечённых инвестиций.
            </p>
          </div>
        </div>
      </section>

      <section className={`${styles.app__section} ${styles['app__section--soft']}`} id="competencies">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--tight']}`}>
            <span className={styles.app__badge}>Компетенции компаний группы</span>
          </div>
          <div className={styles.app__compGrid}>
            <div className={styles.app__compItem}>
              <div className={styles.app__compNum}>1</div>
              <h3>ООО «Технологии скоринга»</h3>
              <div className={styles.app__compLogo}>
                <img src={mintsifry} alt="Технологии скоринга"/>
              </div>
              <p>
                Торговая марка Rescore. Высокотехнологичная IT-компания, предоставляющая услуги агрегатора
                данных. Технологии автоматического сбора, анализа скоринговых данных и&nbsp;мониторинга
                платёжеспособности.
              </p>
              <p>
                Услуги Rescore востребованы не&nbsp;только внутри контура ГК&nbsp;«Кономика»,
                но&nbsp;и&nbsp;в&nbsp;целом на&nbsp;рынке.
              </p>
            </div>

            <div className={styles.app__compItem}>
              <div className={styles.app__compNum}>2</div>
              <h3>ООО «Кономика»</h3>
              <div className={styles.app__compLogo}>
                <img src={logoSkolkovo} alt="Кономика"/>
              </div>
              <p>
                Высокотехнологичная IT-компания, предоставляющая услуги CRM-системы для&nbsp;сделок
                с&nbsp;долговыми требованиями. Использует собственные программные решения, покрывающие весь
                спектр требований участников сделок с&nbsp;дебиторской задолженностью.
              </p>
              <p>
                С&nbsp;помощью услуг ООО&nbsp;«Кономика» заключено более 450&nbsp;сделок
                по&nbsp;купле-продаже долговых требований на&nbsp;сумму свыше 7&nbsp;млрд&nbsp;₽.
              </p>
            </div>

            <div className={styles.app__compItem}>
              <div className={styles.app__compNum}>3</div>
              <h3>ООО «Про&nbsp;Фактор»</h3>
              <div className={styles.app__compLogo}/>
              <p>
                Юридическая компания, которая занимается взысканием. Более 9&nbsp;лет экспертизы
                во&nbsp;взыскании: в&nbsp;сделках, связанных с&nbsp;ГК&nbsp;«Кономика», взыскано порядка
                8&nbsp;млрд&nbsp;₽. Размер дебиторской задолженности, с&nbsp;которой «Про&nbsp;Фактор» сейчас
                работает в&nbsp;суде,&nbsp;— 2,3&nbsp;млрд&nbsp;₽.
              </p>
              <p>
                На&nbsp;сегодняшний день это профессиональный взыскатель, участвовавший более чем
                в&nbsp;700&nbsp;арбитражных делах.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.app__section} id="how">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--center']}`}>
            <span className={styles.app__badge}>Как начать инвестировать</span>
            <h2 className={styles.app__secTitle}>
              Четыре простых шага к&nbsp;
              <span className={styles.app__accent}>первому доходу</span>
            </h2>
            <p className={styles.app__secText}>Всё происходит онлайн в&nbsp;личном кабинете.</p>
          </div>

          <div className={styles.app__steps}>
            {startSteps.map((step, index) => (
              <div className={styles.app__step} key={step.title}>
                <div className={styles.app__stepNum}>{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>

          <div className={styles.app__stepsCta}>
            <CustomLink
              to={RouteDictionary.ENTER}
              className={`${styles.app__btn} ${styles['app__btn--primary']}`}
            >
              Создать личный кабинет
            </CustomLink>
          </div>
        </div>
      </section>

      <section className={`${styles.app__section} ${styles['app__section--soft']}`} id="about">
        <div className={`${styles.app__container} ${styles.app__reliableGrid}`}>
          <div>
            <div className={`${styles.app__secHead} ${styles['app__secHead--narrow']}`}>
              <span className={styles.app__badge}>Что это и почему надёжно</span>
              <h2 className={styles.app__secTitle}>
                Инвестиционные возможности,{' '}
                <span className={styles.app__accent}>которые раньше были доступны только банкам</span>
              </h2>
            </div>
            <p><b>Как выглядит классический факторинг</b></p>
            <p>
              Бизнес поставляет товар или услугу крупному контрагенту, который платит с&nbsp;отсрочкой
              от&nbsp;30 до&nbsp;90&nbsp;дней. На&nbsp;время отсрочки у&nbsp;поставщика возникает кассовый
              разрыв. Чтобы закрыть его, поставщик уступает платформе право на&nbsp;будущую оплату
              со&nbsp;скидкой. Инвесторы финансируют сделку сейчас, а&nbsp;доход получают из&nbsp;оплаты
              контрагента. Этот инструмент называется факторингом.
            </p>
            <p><b>В&nbsp;чём отличие сделки на&nbsp;платформе</b></p>
            <p>
              Сделка имеет аналогичный юридический итог, но&nbsp;вместо договора факторинга оформляется
              двумя отдельными договорами: договором займа и&nbsp;договором обеспечительной уступки.
            </p>
          </div>

          <div className={styles.app__collateralCard}>
            <div className={styles.app__ccTitle}>
              {Icon.shield}
              Пример обеспечения сделки
            </div>
            <div>
              <div className={styles.app__barRow}>
                <div className={styles.app__barLabel}>
                  <span className={styles.app__barName}>Сумма финансирования</span>
                  <span className={styles.app__barValue}>16,1 млн&nbsp;₽</span>
                </div>
                <div className={`${styles.app__bar} ${styles['app__bar--grey']}`}>71%</div>
              </div>
              <div className={styles.app__barRow}>
                <div className={styles.app__barLabel}>
                  <span className={styles.app__barName}>Обеспечение</span>
                  <span className={styles.app__barValue}>22,2 млн&nbsp;₽</span>
                </div>
                <div className={`${styles.app__bar} ${styles['app__bar--green']}`}>100%</div>
              </div>
            </div>
            <div className={styles.app__ccStats}>
              <div className={styles.app__ccStat}>
                <span className={styles.app__ccKey}>Доходность инвестора</span>
                <span className={`${styles.app__ccValue} ${styles['app__ccValue--green']}`}>26% годовых</span>
              </div>
              <div className={styles.app__ccStat}>
                <span className={styles.app__ccKey}>Срок сделки</span>
                <span className={styles.app__ccValue}>3&nbsp;месяца</span>
              </div>
              <div className={styles.app__ccStat}>
                <span className={styles.app__ccKey}>Покрытие обеспечением (LTV)</span>
                <span className={styles.app__ccValue}>72%</span>
              </div>
            </div>
            <div className={styles.app__collateralNote}>
              Обеспечение составляет 138% от&nbsp;суммы финансирования
            </div>
          </div>
        </div>
      </section>

      <section className={styles.app__section} id="example">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--center']}`}>
            <span className={styles.app__badge}>Пример проекта</span>
            <h2 className={styles.app__secTitle}>
              Как выглядит сделка <span className={styles.app__accent}>на&nbsp;витрине</span>
            </h2>
            <p className={styles.app__secText}>Реальный пример проекта, доступного инвесторам платформы.</p>
          </div>

          <div className={styles.app__projectWrap}>
            <div className={styles.app__projectCard}>
              <div className={styles.app__pcHead}>
                <div className={styles.app__pcName}>
                  Пример сделки
                  <small>Финансирование поставки под оплату контрагента</small>
                </div>
                <div className={styles.app__pcTag}>Доходность 26% годовых</div>
              </div>
              <div className={styles.app__pcRows}>
                {projectRows.map((row, index) => (
                  <div className={styles.app__pcRow} key={index}>
                    <span className={styles.app__pcKey}>{row.label}</span>
                    <span
                      className={`${styles.app__pcValue} ${row.green ? styles['app__pcValue--green'] : ''}`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.app__ltvNote}>
              <span className={styles.app__ltvIco}>{Icon.shield}</span>
              <p>
                <b>Что такое LTV 72%.</b> Сумма финансирования составляет 72% от&nbsp;стоимости обеспечения.
                Обеспечение превышает вложения инвесторов и&nbsp;служит защитой на&nbsp;случай неплатежа.
              </p>
            </div>
            <div className={styles.app__ltvNote}>
              <span className={styles.app__ltvIco}>{Icon.shield}</span>
              <p>
                <b>Номинальный счёт.</b> Специальный банковский счёт, средства на&nbsp;котором юридически
                принадлежат инвесторам.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={`${styles.app__section} ${styles['app__section--soft']}`} id="collection">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--wide']}`}>
            <span className={styles.app__badge}>Работа с&nbsp;долгом</span>
            <h2 className={styles.app__secTitle}>
              Просрочку ведёт{' '}
              <span className={styles.app__accent}>собственная компания по&nbsp;взысканию</span>
            </h2>
            <p className={styles.app__secText}>
              Все процедуры, связанные со&nbsp;взаимодействием с&nbsp;заёмщиком, допустившим просрочку, берёт
              на&nbsp;себя компания Группы Conomica ООО&nbsp;«Про&nbsp;Фактор»&nbsp;— профильная юридическая
              компания по&nbsp;досудебной претензионной работе и&nbsp;судебному взысканию.
            </p>
          </div>

          <div className={styles.app__riskGrid}>
            <div className={`${styles.app__riskBig} ${styles['app__riskBig--illus']}`}>
              <svg className={styles.app__riArt} viewBox="0 0 120 120" fill="none" stroke="currentColor"
                strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="60" cy="15" r="3.4"/>
                <path d="M60 18v78"/>
                <path d="M30 34h60"/>
                <path d="M30 34 19 55"/>
                <path d="M30 34 41 55"/>
                <path d="M17 55a13 13 0 0 0 26 0"/>
                <path d="M90 34 79 55"/>
                <path d="M90 34 101 55"/>
                <path d="M77 55a13 13 0 0 0 26 0"/>
                <path d="M47 96h26"/>
                <path d="M40 104h40"/>
              </svg>
              <div className={styles.app__riCap}>
                Собственный штат юристов, специализирующихся на&nbsp;взыскании и&nbsp;МСБ
              </div>
            </div>
            <div className={styles.app__levels}>
              {collectionLevels.map((level, index) => (
                <div className={styles.app__level} key={index}>
                  <div className={styles.app__lvNum}>{index + 1}</div>
                  <div>
                    <h3>{level.title}</h3>
                    <p>{level.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`${styles.app__ltvNote} ${styles['app__ltvNote--spaced']}`}>
            <span className={styles.app__ltvIco}>{Icon.shield}</span>
            <p>
              <b>Операционные расходы на&nbsp;взыскание берёт оператор.</b> Взысканием занимается команда
              Группы, а&nbsp;не&nbsp;инвестор. Это снижает риск, но&nbsp;не&nbsp;устраняет его полностью:
              возврат средств не&nbsp;гарантирован.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.app__section} id="calculator">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--center']}`}>
            <span className={styles.app__badge}>Калькулятор</span>
            <h2 className={styles.app__secTitle}>
              Рассчитайте свой <span className={styles.app__accent}>доход</span>
            </h2>
          </div>
          <Calculator/>
        </div>
      </section>

      <section className={styles.app__metrics} id="metrics">
        <div className={styles.app__container}>
          <div className={styles.app__metricsStrip}>
            {metrics.map((metric, index) => (
              <div className={styles.app__msItem} key={index}>
                <div className={styles.app__msValue}>{metric.value}</div>
                <div className={styles.app__msLabel}>{metric.label}</div>
              </div>
            ))}
          </div>
          <div className={styles.app__metricsNote}>
            {Icon.alert}
            Кономика в&nbsp;цифрах
          </div>
        </div>
      </section>

      <section className={styles.app__section} id="evolution">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--center']}`}>
            <span className={styles.app__badge}>Путь компании</span>
            <h2 className={styles.app__secTitle}>
              От факторинговой компании{' '}
              <span className={styles.app__accent}>до инвестиционной платформы</span>
            </h2>
            <p className={styles.app__secText}>
              Три этапа за 9&nbsp;лет&nbsp;— сначала экспертиза, потом технология, затем лицензированная
              платформа.
            </p>
          </div>

          <div className={styles.app__epochs}>
            {epochs.map((epoch) => (
              <div
                className={`${styles.app__epoch} ${epoch.hero ? styles['app__epoch--hero'] : ''}`}
                key={epoch.range}
              >
                <div className={styles.app__epochRange}>{epoch.range}</div>
                <h3>{epoch.title}</h3>
                <p>{epoch.text}</p>
                <div className={styles.app__chips}>
                  {epoch.chips.map((chip) => (
                    <span className={styles.app__chip} key={chip}>{chip}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${styles.app__section} ${styles['app__section--soft']}`} id="security">
        <div className={styles.app__container}>
          <div className={styles.app__secHead}>
            <span className={styles.app__badge}>Защита и&nbsp;надзор</span>
            <h2 className={styles.app__secTitle}>
              Как устроена <span className={styles.app__accent}>защита средств</span>
            </h2>
            <p className={styles.app__secText}>
              Юридические и&nbsp;технические механизмы, закреплённые в&nbsp;документах платформы.
            </p>
          </div>

          <div className={styles.app__protectGrid}>
            {protections.map((item, index) => (
              <div className={styles.app__protect} key={index}>
                <div className={styles.app__protectPic}>{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>

          <div className={styles.app__divider}/>

          <div className={`${styles.app__secHead} ${styles['app__secHead--center']} ${styles['app__secHead--disclosure']}`}>
            <span className={styles.app__badge}>Прозрачность</span>
            <h2 className={styles.app__secTitle}>
              Что вы видите <span className={styles.app__accent}>по&nbsp;каждой сделке</span>
            </h2>
            <p className={styles.app__secText}>
              До&nbsp;вложения по&nbsp;проекту раскрывается информация о&nbsp;заёмщике и&nbsp;условиях сделки.
            </p>
          </div>

          <ul className={styles.app__factList}>
            {disclosureFacts.map((fact, index) => (
              <li key={index}>
                <span className={styles.app__ck}>✓</span>
                <span>{fact}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={styles.app__section} id="team">
        <div className={styles.app__container}>
          <div className={`${styles.app__secHead} ${styles['app__secHead--center']}`}>
            <span className={styles.app__badge}>Команда</span>
            <h2 className={styles.app__secTitle}>
              Команда с&nbsp;опытом{' '}
              <span className={styles.app__accent}>в&nbsp;финансах, праве и&nbsp;взыскании</span>
            </h2>
          </div>

          <div className={styles.app__teamGrid}>
            {team.map((member) => (
              <div className={styles.app__teamCard} key={member.name}>
                <div className={styles.app__teamAva}>
                  <img
                    src={member.photo}
                    alt={member.name}
                    style={member.photoPosition ? {objectPosition: member.photoPosition} : undefined}
                  />
                </div>
                <h3>{member.name}</h3>
                <div className={styles.app__teamRole}>{member.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${styles.app__section} ${styles['app__section--soft']}`} id="start">
        <div className={styles.app__container}>
          <div className={styles.app__finalCta}>
            <h2>Начните инвестировать от&nbsp;10&nbsp;000&nbsp;₽</h2>
            <p>Зарегистрируйтесь и&nbsp;соберите портфель из&nbsp;проверенных сделок с&nbsp;обеспечением.</p>
            <CustomLink
              to={RouteDictionary.ENTER}
              className={`${styles.app__btn} ${styles['app__btn--white']}`}
            >
              Зарегистрироваться
            </CustomLink>
            <div className={styles.app__fcDisclaimer}>
              Инвестиции связаны с&nbsp;риском, включая риск потери вложенных средств. Доходность
              не&nbsp;гарантирована.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default App;
